/**
 * Wallet connectors configuration
 *
 * This file now imports from the centralized chain config.
 * Chain is determined by REACT_APP_CHAIN_ID environment variable.
 */
import { CoinbaseWallet } from "@web3-react/coinbase-wallet";
import { Web3ReactHooks } from "@web3-react/core";
import { MetaMask } from "@web3-react/metamask";
import type { Network } from "@web3-react/network";
import { WalletConnect as WalletConnectV2 } from "@web3-react/walletconnect-v2";
import {
  AllAltChainIdEnum,
  AllChainIdEnum,
  ChainIdEnum,
} from "../../../enums/chain";
import {
  getChainIdFromEnv,
  getSupportedDepositChains as getCentralizedSupportedDepositChains,
} from "../../../config/chains";
import { WalletEnum } from "../types";
import { coinbaseWallet, hooks as coinbaseWalletHooks } from "./coinbaseWallet";
import { metaMask, hooks as metaMaskHooks } from "./metamask";
import { network, hooks as networkHooks } from "./network";
import {
  walletConnectV2,
  hooks as walletConnectV2Hooks,
} from "./walletConnectV2";
import {
  eip6963Connector,
  hooks as eip6963Hooks,
} from "./eip6963";

export const supportedChainIds = [
  ...AllChainIdEnum.filter((v) => v !== ChainIdEnum.NONE),
  ...AllAltChainIdEnum,
];

/**
 * Get supported chain from environment
 * Now uses REACT_APP_CHAIN_ID
 */
const getSupportedChain = (): ChainIdEnum => {
  return getChainIdFromEnv();
};

/**
 * Get supported deposit chains based on current chain
 * Now uses centralized config
 */
const getSupportedDepositChains = (): ChainIdEnum[] => {
  return getCentralizedSupportedDepositChains();
};

export const supportedChainId: ChainIdEnum = getSupportedChain();
export const supportedSigningChainIds: ChainIdEnum[] =
  getSupportedDepositChains();

export const allConnectors: [
  MetaMask | WalletConnectV2 | CoinbaseWallet | Network | typeof eip6963Connector,
  Web3ReactHooks
][] = [
  [metaMask, metaMaskHooks],
  [walletConnectV2, walletConnectV2Hooks],
  [coinbaseWallet, coinbaseWalletHooks],
  [eip6963Connector, eip6963Hooks],
  [network, networkHooks],
];

export const walletToConnector: Record<
  WalletEnum,
  () => WalletConnectV2 | MetaMask | CoinbaseWallet | typeof eip6963Connector | undefined
> = {
  [WalletEnum.METAMASK]: () => metaMask,
  [WalletEnum.WALLETCONNECT]: () => walletConnectV2,
  [WalletEnum.WALLETLINK]: () => coinbaseWallet,
  [WalletEnum.BRAVE]: () => metaMask,
  [WalletEnum.EIP6963]: () => eip6963Connector,
  // SDK wallets don't use a web3-react connector — useWallet.activate branches to
  // the adapter before consulting this map. Present only to keep the record total.
  [WalletEnum.PRIVY]: () => undefined,
  [WalletEnum.PARA]: () => undefined,
  [WalletEnum.DOGEOS]: () => undefined,
};

export { eip6963Connector };
